// src/pages/Home.jsx
import { useEffect, useState } from 'react';
import { Container, Typography, Box, Avatar, Button, IconButton, Menu, MenuItem } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  ArrowForward,
  FiberManualRecord,
  MenuBook,
  CallSplit,
  CheckCircle,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import homepageImage from '../homepageImage.jpg';
import FeatureShowcase from '../components/home/FeatureShowcase';

// Top Authors data
const topAuthors = [
  {
    id: 1,
    name: "Sanchit Howdy",
    books: 5,
    photo: "/images/authors/author1.jpg"
  },
  {
    id: 2,
    name: "Paulo Coelho",
    books: 12,
    photo: "/images/authors/author2.jpg"
  },
  {
    id: 3,
    name: "Jane Austen",
    books: 8,
    photo: "/images/authors/author3.jpg"
  }
];

// Sorted by books desc, for the "Right now" ticker (separate from the full showcase below)
const topAuthorsByActivity = [...topAuthors].sort((a, b) => b.books - a.books);

const headlines = [
  "Your knowledge. Always in motion.",
  "Publishing, cut with the community.",
  "Every book, a living conversation.",
  "A lifetime of ideas, one living manuscript."
];

const ACTIVITY_ICONS = { branch: CallSplit, check: CheckCircle, book: MenuBook };

// Fallback content shown until a real activity feed exists. Every entry must come from
// an author who opted in via a signup-time consent checkbox before it can go live (see ticket).
const FALLBACK_ACTIVITY = [
  { type: 'branch', text: 'New idea branch on "The Long Ledger"' },
  { type: 'check', text: 'Reviewer accepted a revision on "Notes on Attention"' },
  { type: 'book', text: '"A Quiet Method" just published' }
];

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const marqueeScroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// Shared style for the top-nav tabs on the dark (primary) masthead: solid white text
// with a shadow so it stays legible over the busy hero photo, gold (secondary) on hover.
const navLinkSx = {
  color: '#FFFFFF',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 16,
  letterSpacing: '0.04em',
  textShadow: '0 1px 4px rgba(0,0,0,0.65)',
  transition: 'color 0.2s ease',
  '&:hover': { color: 'secondary.main' },
};

// Dark-mode card schema shared by every card on the page — a dark plum surface
// (in the primary.main family), lightly transparent so the hero background still shows
// through, with a faint light border instead of white/#F5F3FB.
const darkCardSx = { backgroundColor: 'rgba(34,30,56,0.88)', border: '0.5px solid rgba(255,255,255,0.14)' };
const TEXT_MUTED = 'rgba(255,255,255,0.6)';

export default function Home() {
  const { user, startCognito } = useAuth();
  const navigate = useNavigate();
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [activity, setActivity] = useState(FALLBACK_ACTIVITY);
  const [navAnchor, setNavAnchor] = useState(null);

  // Already-logged-in visitors skip the Cognito redirect entirely and go straight in.
  const handleAuthCta = () => (user ? navigate('/dashboard') : startCognito('signup'));

  // One source for both the desktop row and the mobile menu, so they can't drift apart.
  const navItems = [
    { label: 'Home', onClick: () => navigate('/') },
    { label: 'About', onClick: () => navigate('/about') },
    { label: 'Pricing', onClick: () => navigate('/pricing') },
    { label: 'Community', onClick: () => navigate('/community') },
  ];

  // Rotate the hero headline
  useEffect(() => {
    const id = setInterval(() => {
      setHeadlineIndex((i) => (i + 1) % headlines.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  // Refresh the "right now" activity ticker hourly. No backend endpoint exists yet,
  // so this silently keeps the static fallback until /api/activity-feed is built.
  useEffect(() => {
    let cancelled = false;
    async function refreshActivity() {
      try {
        const res = await fetch('/api/activity-feed');
        if (!res.ok) throw new Error('activity feed endpoint not available yet');
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) setActivity(data);
      } catch {
        // stay on FALLBACK_ACTIVITY
      }
    }
    refreshActivity();
    const id = setInterval(refreshActivity, 60 * 60 * 1000); // hourly, per ticket
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <Box
      sx={{
        backgroundImage: `url(${homepageImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Navigation Bar — the "/" route doesn't render the global app Navbar, so this
          page owns its own bar, matching the rest of the site's brand colors */}
      <Box sx={{ py: 1.5 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr auto', md: '1fr auto 1fr' },
              alignItems: 'center'
            }}
          >
            {/* revisionari-logo.png is welcome-bg.jpg with the white background keyed out and
                the margin cropped — colors are the originals. Swap this src (and the one in
                Pricing.jsx) if a proper SVG ever arrives. */}
            <Box
              component={Link}
              to="/"
              aria-label="ReVisionari home"
              sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, lineHeight: 0 }}
            >
              <Box
                component="img"
                src="/images/revisionari-logo.png"
                alt="ReVisionari"
                sx={{ height: { xs: 34, sm: 42, md: 52 }, width: 'auto', display: 'block' }}
              />
            </Box>

            {/* Desktop nav — centered against the full bar, not just the space left of the
                button. The full row needs ~500px, so it only fits from md up */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3, justifySelf: 'center' }}>
              {navItems.map((item) => (
                <Typography key={item.label} sx={navLinkSx} onClick={item.onClick}>
                  {item.label}
                </Typography>
              ))}
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifySelf: 'end' }}>
              <Button variant="contained" color="secondary" onClick={handleAuthCta}>
                Login/Sign up
              </Button>
            </Box>

            {/* Mobile nav — keeps the primary CTA visible, everything else behind the menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, justifySelf: 'end' }}>
              <Button variant="contained" color="secondary" size="small" onClick={handleAuthCta}>
                Login/Sign up
              </Button>
              <IconButton
                onClick={(e) => setNavAnchor(e.currentTarget)}
                aria-label="Open menu"
                aria-expanded={Boolean(navAnchor)}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
            <Menu
              anchorEl={navAnchor}
              open={Boolean(navAnchor)}
              onClose={() => setNavAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {navItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    setNavAnchor(null);
                    item.onClick?.();
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Container>
      </Box>

      {/* Hero — full-bleed dark block sitting flush under the nav, matching the reference */}
      <Box>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 4, py: { xs: 4, md: 6 } }}>
          <Box>
            <Typography
              key={headlineIndex}
              sx={{
                fontFamily: 'Cambria, Georgia, serif',
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.25,
                minHeight: { md: 140 },
                fontSize: { xs: '1.8rem', md: '2.25rem' },
                animation: `${fadeIn} 0.6s ease`
              }}
            >
              {headlines[headlineIndex]}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', my: 3, lineHeight: 1.6, maxWidth: 420 }}>
              Draft, revise, and branch ideas live with a community that reviews as you write.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForward />}
              onClick={() => startCognito('signup')}
            >
              Start writing
            </Button>
          </Box>

          <Box sx={{ ...darkCardSx, borderRadius: 3, p: 3 }}>
            <Typography sx={{ fontSize: 11, color: TEXT_MUTED, mb: 1.5 }}>Chapter 4 &middot; live</Typography>
            <Typography sx={{ fontSize: 14, color: '#FFFFFF', lineHeight: 1.9 }}>
              The manuscript opened where she&rsquo;d left it, mid&#8209;sentence, the way a river holds its shape around a stone
              <Box component="span" sx={{ animation: `${blink} 1s step-end infinite` }}>|</Box>
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box
                component="span"
                sx={{
                  backgroundColor: '#F6ECC9',
                  color: '#6B4F14',
                  fontSize: 11,
                  px: 1.5,
                  py: 0.6,
                  borderRadius: 5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <FiberManualRecord sx={{ fontSize: 9, color: 'secondary.main' }} /> 2 reviewing now
              </Box>
            </Box>
          </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Fanned feature cards — hover popovers + click-to-open modal demos live here */}
        <FeatureShowcase />

        {/* Right now on ReVisionari */}
        {/* Full-bleed inside the Container: the negative margin must match the
            Container's own gutter exactly (16px xs, 24px sm+), or the block
            overhangs the viewport. -4 (32px) against a 24px gutter is what
            made this page scroll sideways at 1024. */}
        <Box sx={{ mx: { xs: -2, sm: -3 }, px: { xs: 2, sm: 3 }, py: 5, ...darkCardSx, borderRadius: 3, mb: 8 }}>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Cambria, Georgia, serif', fontSize: 22, color: '#FFFFFF', mb: 3.5 }}>
            Right now on ReVisionari
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
            {/* Real data, links to author profiles */}
            <Box sx={{ ...darkCardSx, borderRadius: 3, p: 2.5 }}>
              <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 1.75 }}>Top authors</Typography>
              {topAuthorsByActivity.map((author, i) => (
                <Box
                  key={author.id}
                  component={Link}
                  to={`/author/${author.id}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: i < topAuthorsByActivity.length - 1 ? 1.5 : 0, textDecoration: 'none' }}
                >
                  <Avatar sx={{ width: 26, height: 26, fontSize: 10, fontWeight: 700, bgcolor: i === 0 ? 'secondary.main' : 'primary.light' }}>
                    {author.name.split(' ').map((p) => p[0]).join('')}
                  </Avatar>
                  <Typography sx={{ fontSize: 13, color: '#FFFFFF', flex: 1 }}>{author.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: TEXT_MUTED }}>{author.books} books</Typography>
                </Box>
              ))}
            </Box>

            {/* No book/branch stats are tracked anywhere in the app yet */}
            <Box sx={{ ...darkCardSx, borderRadius: 3, p: 2.5 }}>
              <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 1.75 }}>Top books</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 96 }}>
                <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontStyle: 'italic' }}>
                  Coming soon — no book/branch stats tracked yet
                </Typography>
              </Box>
            </Box>

            {/* No acceptance-rate stats are tracked anywhere in the app yet */}
            <Box sx={{ ...darkCardSx, borderRadius: 3, p: 2.5 }}>
              <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 1.75 }}>Top collaborators</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 96 }}>
                <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontStyle: 'italic' }}>
                  Coming soon — no acceptance-rate stats tracked yet
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Live activity marquee — refreshes hourly from /api/activity-feed once that
            endpoint exists; falls back to static content until then */}
        <Box sx={{ mx: { xs: -2, sm: -3 }, bgcolor: 'surface.ink', overflow: 'hidden', py: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', width: 'max-content', whiteSpace: 'nowrap', animation: `${marqueeScroll} 20s linear infinite` }}>
            {[...activity, ...activity].map((item, i) => {
              const Icon = ACTIVITY_ICONS[item.type] ?? CallSplit;
              return (
                <Box key={i} component="span" sx={{ color: '#FFFFFF', fontSize: 12, mr: 6, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Icon sx={{ fontSize: 13, color: 'secondary.main' }} />
                  {item.text}
                </Box>
              );
            })}
          </Box>
        </Box>

      </Container>

      {/* No custom footer here — App.js renders the global <Footer /> unconditionally
          on every route, including "/", right after this component. */}
    </Box>
  );
}
